import React, { useState, useEffect } from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import Constant from "../../Constant";
import Apa1 from "../../Assets/images/apa1.png";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "92%",
  maxWidth: 420,
  bgcolor: "#FFFFFF",
  borderRadius: "20px",
  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
  outline: "none",
  p: "24px",
  maxHeight: "90vh",
  overflowY: "auto",
};

function UnitSelectorModal({
  open,
  handleClose,
  details = {},
  initialUnit = 10,
  onContinue,
}) {
  const [unit, setUnit] = useState(initialUnit || 10);

  useEffect(() => {
    if (open) {
      setUnit(initialUnit || 10);
    }
  }, [open, initialUnit]);

  const title = details.title || "Lekki Phase 1 Plot";
  const type = details.type || details.propertyType || "Land";
  const pricePerUnit = details.pricePerUnit || details.price || 25000;
  let availableUnits = Number(
    details.unitsLeft !== undefined
      ? details.unitsLeft
      : details.unitRemaining !== undefined
        ? details.unitRemaining
        : details.plotsAvailable !== undefined
          ? details.plotsAvailable
          : 33
  );

  if (isNaN(availableUnits) || availableUnits <= 0) {
    availableUnits = 100; // safe fallback so buttons are not disabled incorrectly
  }

  let displayImage = Apa1;
  if (details.image) {
    displayImage = details.image;
  } else if (
    Array.isArray(details.projectImages) &&
    details.projectImages.length > 0
  ) {
    displayImage =
      details.projectImages[0]?.imageUrl || details.projectImages[0];
  } else if (Array.isArray(details.imageUrl) && details.imageUrl.length > 0) {
    displayImage = details.imageUrl[0];
  } else if (typeof details.imageUrl === "string" && details.imageUrl) {
    displayImage = details.imageUrl;
  }

  const quickOptions = [1, 2, 4, 10];

  const handleIncrement = () => {
    if (unit < availableUnits) {
      setUnit((prev) => prev + 1);
    }
  };

  const handleDecrement = () => {
    if (unit > 1) {
      setUnit((prev) => prev - 1);
    }
  };

  const totalValue = unit * pricePerUnit;

  const handleContinueClick = () => {
    if (onContinue) {
      onContinue(unit);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 400,
        sx: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
      }}
    >
      <Fade in={open}>
        <Box sx={modalStyle}>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleClose}
              className="flex items-center gap-2 text-[#1E222B] font-[700] text-[15px] cursor-pointer hover:opacity-80 transition"
            >
              <ArrowBackIcon sx={{ fontSize: 18, color: "#1E222B" }} />
              <span>Back</span>
            </button>

            <button
              type="button"
              onClick={handleClose}
              className="w-9 h-9 rounded-full bg-[#18191B] hover:bg-black text-white flex items-center justify-center cursor-pointer transition shadow-xs"
              aria-label="Close"
            >
              <CloseIcon sx={{ fontSize: 17 }} />
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-3.5 shadow-2xs flex items-center gap-3.5 mt-5">
            <div className="w-[66px] h-[66px] rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
              <img
                src={displayImage}
                alt={title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[#1E222B] font-[700] text-[15px] leading-tight truncate">
                {title}
              </h3>
              <p className="text-[#808285] text-[12px] font-[500] mt-0.5">
                ₦{Constant.FormatNumber(pricePerUnit)} per unit
              </p>
              <p className="text-[11px] text-[#808285] font-[600] tracking-wide mt-1">
                AVAILABLE :{" "}
                <span className="font-[800] text-[#1E222B]">
                  {availableUnits} UNITS
                </span>
              </p>
            </div>
            <span className="px-2.5 py-0.5 rounded-[5px] text-center bg-[#76D400] text-white text-[11px] font-bold uppercase tracking-wide flex-shrink-0">
              {type}
            </span>
          </div>

          {/* Quick Select */}
          <div className="mt-5">
            <p className="text-[11px] font-[700] text-[#808285] uppercase tracking-wider mb-2.5">
              QUICK SELECT
            </p>
            <div className="grid grid-cols-4 gap-2">
              {quickOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setUnit(opt)}
                  className={`py-2 px-2 text-center text-[13px] font-[700] rounded-full transition-all cursor-pointer ${unit === opt
                      ? "bg-[#00BC67] text-white shadow-xs"
                      : "bg-[#F3F6F9] text-[#1E222B] hover:bg-gray-100"
                    }`}
                >
                  {opt} unit{opt > 1 ? "s" : ""}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Unit Selector */}
          <div className="mt-5">
            <p className="text-[11px] font-[700] text-[#808285] uppercase tracking-wider mb-2.5">
              CUSTOM
            </p>
            <div className="bg-[#F8FAFC] rounded-2xl p-2.5 flex items-center justify-between border border-gray-100/60">
              <button
                type="button"
                onClick={handleDecrement}
                disabled={unit <= 1}
                className="w-10 h-10 rounded-full bg-[#CCF2E1] hover:bg-[#b2ebd0] text-[#00BC67] flex items-center justify-center font-[700] text-[22px] cursor-pointer transition active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed leading-none select-none pb-0.5"
                aria-label="Decrease units"
              >
                -
              </button>

              <span className="text-[20px] font-[800] text-[#1E222B]">
                {unit}
              </span>

              <button
                type="button"
                onClick={handleIncrement}
                disabled={unit >= availableUnits}
                className="w-10 h-10 rounded-full bg-[#00BC67] hover:bg-[#00a85c] text-white flex items-center justify-center font-[700] text-[22px] cursor-pointer transition active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed leading-none select-none pb-0.5"
                aria-label="Increase units"
              >
                +
              </button>
            </div>
            <p className="text-[11px] text-[#808285] text-center mt-1.5 font-[400]">
              of {availableUnits} available units
            </p>
          </div>

          {/* Price Breakdown Card */}
          <div className="bg-[#FAFBFC] rounded-2xl p-4 border border-gray-100/80 mt-4 space-y-2">
            <div className="flex justify-between items-center text-[12px]">
              <span className="text-[#999FA9] font-[500]">Price Per Unit</span>
              <span className="text-[#1E222B] font-[700]">
                ₦{Constant.FormatNumber(pricePerUnit)}
              </span>
            </div>

            <div className="border-t border-gray-200/50 pt-2 flex justify-between items-center text-[12px]">
              <span className="text-[#999FA9] font-[500]">Total Value</span>
              <span className="text-[#1E222B]  text-[14px] font-[700]">
                ₦{Constant.FormatNumber(totalValue)}
              </span>
            </div>
          </div>

          {/* Continue Button */}
          <button
            type="button"
            onClick={handleContinueClick}
            disabled={!unit || unit <= 0}
            className="w-full py-3.5 rounded-xl bg-[#00BC67] hover:bg-[#00a85c] disabled:opacity-50 disabled:cursor-not-allowed text-white font-[700] text-[15px] cursor-pointer text-center mt-6"
          >
            Continue - ₦{Constant.FormatNumber(totalValue)}
          </button>
        </Box>
      </Fade>
    </Modal>
  );
}

export default UnitSelectorModal;
